/*
 * <card-loop-video>
 *
 * Collection-card port of the PDP looping video (product-loop-video.js): an
 * auto-playing, muted, looping product video in the card carousel's 3rd slot,
 * driven by the custom.pdp_loop_video metafield. The play/pause toggle sits
 * bottom-LEFT (quick add owns the card's bottom-right).
 *
 * Performance: the server renders NO <video> — just a lazy poster image and
 * data attributes. The <video> is created on demand, the first time the
 * shopper engages that card's carousel (scrolls its slider) or the slide
 * itself becomes visible. A 36-card grid therefore makes zero video requests
 * until a card is actually swiped. Playback runs only while the slide is at
 * least half visible (the IntersectionObserver respects the horizontal
 * scroller's clipping, so a slide swiped away or a card scrolled off the page
 * both pause).
 *
 * Swap-safe like the PDP element: all wiring lives in connectedCallback /
 * disconnectedCallback, so cards replaced by AJAX filtering or the color
 * swatch swap re-initialise cleanly with no leaked observers or listeners.
 *
 * One video at a time (client, 2026-09-21): starting any card's video stops
 * the card video that played before it, rewinds it, and puts that card back on
 * its first slide. See onPlay() and stopAndReset().
 *
 * Keyboard focus follows the hand-over between the card-level jump button and
 * this element's toggle, which take turns in the same corner of the card. See
 * takeFocusFromJump() and returnFocusToJump().
 */
if (!customElements.get('card-loop-video')) {
  /*
   * The card whose video started most recently. Page-wide on purpose: the rule
   * has to hold across sections (the homepage runs two carousels) and across
   * every card surface. Block-scoped rather than a window global because only
   * the first run of this file enters this block, so there is only ever one.
   */
  let activeCardVideo = null;

  /*
   * True when `el` has focus AND that focus came from the keyboard, i.e. it is
   * showing a focus ring. A pointer press never qualifies: Chrome focuses a
   * clicked button without :focus-visible, and Safari does not focus it at all,
   * which is what keeps the focus hand-overs below to keyboard users. matches()
   * throws in an engine without :focus-visible, which counts as "no".
   */
  const hasKeyboardFocus = (el) => {
    if (!el || document.activeElement !== el) return false;
    try {
      return el.matches(':focus-visible');
    } catch (e) {
      return false;
    }
  };

  customElements.define(
    'card-loop-video',
    class CardLoopVideo extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.card-loop-video__toggle');
        this.ring = this.querySelector('.card-loop-video__ring-progress');
        this.link = this.querySelector('a');
        if (!this.toggle || !this.dataset.videoSrc) return;

        // The card carries the video-slide state class and holds the card-level
        // play button (see card-product-image-slider.css).
        this.card = this.closest('.card');

        this.video = null;
        this.userPaused = false;
        this.held = false; // see stopAndReset()
        this.homing = false; // see stopAndReset()
        this.jumpPressedFromKeyboard = false; // see takeFocusFromJump()
        this.inView = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.labelPlay = this.toggle.dataset.labelPlay || 'Play video';
        this.labelPause = this.toggle.dataset.labelPause || 'Pause video';

        this.onToggleClick = this.onToggleClick.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onScrollerScroll = this.onScrollerScroll.bind(this);
        this.onHomingScroll = this.onHomingScroll.bind(this);
        this.tick = this.tick.bind(this);

        this.toggle.addEventListener('click', this.onToggleClick);

        // Warm signal: the shopper started swiping this card's carousel (touch
        // swipe and the desktop arrows both scroll the list), so start fetching
        // the video before its slide arrives.
        this.scroller = this.closest('.card-image-slider__list');
        if (this.scroller) this.scroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });

        // Play only while the slide is the dominant one on screen; pause
        // otherwise. Any visibility at all also works as a warm signal.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              if (!this.inView) this.held = false;
              // A slide crossing the frame on its way home (see stopAndReset) is
              // not the active one: the jump button stays up and focus stays put.
              const active = this.inView && !this.homing;
              if (this.card) this.card.classList.toggle('card--video-slide-active', active);
              // That class hides the jump button, so keyboard focus leaves it now.
              if (active) this.takeFocusFromJump();
              if (entry.isIntersecting) this.warm();
              if (this.inView) this.maybePlay();
              else if (this.video) this.video.pause();
            });
          },
          { threshold: [0, 0.5, 1] }
        );
        this.observer.observe(this);

        this.setPlayingState(false);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.card) this.card.classList.remove('card--video-slide-active');
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
        if (this.scroller) this.scroller.removeEventListener('scroll', this.onScrollerScroll);
        if (this.video) {
          this.video.removeEventListener('play', this.onPlay);
          this.video.removeEventListener('pause', this.onPause);
          this.video.pause();
        }
        this.stopTicking();
        this.endHoming();
        if (activeCardVideo === this) activeCardVideo = null;
      }

      onScrollerScroll() {
        this.scroller.removeEventListener('scroll', this.onScrollerScroll);
        this.warm();
      }

      // Create the video so it starts buffering, without playing it yet.
      // Reduced-motion users don't get a speculative download — for them the
      // video is only created by an explicit press of the play button.
      warm() {
        if (this.video || this.reducedMotion) return;
        this.createVideo();
      }

      createVideo() {
        if (this.video) return this.video;
        const video = document.createElement('video');
        video.className = 'card-loop-video__video';
        video.muted = true; // property AND attribute: iOS only autoplays muted video
        video.setAttribute('muted', '');
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'auto';
        video.disablePictureInPicture = true;
        video.setAttribute('disableremoteplayback', '');
        video.setAttribute('tabindex', '-1');
        if (this.dataset.videoLabel) video.setAttribute('aria-label', this.dataset.videoLabel);
        const source = document.createElement('source');
        source.src = this.dataset.videoSrc;
        if (this.dataset.videoType) source.type = this.dataset.videoType;
        video.appendChild(source);
        video.addEventListener('play', this.onPlay);
        video.addEventListener('pause', this.onPause);
        (this.link || this).appendChild(video);
        this.video = video;
        return video;
      }

      maybePlay() {
        if (this.userPaused || this.reducedMotion || this.held || this.homing) return;
        const playPromise = this.createVideo().play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // Swallow autoplay rejections (rare for muted inline video).
          playPromise.catch(() => {});
        }
      }

      onToggleClick() {
        if (!this.video || this.video.paused) {
          // Explicit user intent overrides manual-pause, reduced-motion and the
          // hold stopAndReset() leaves behind.
          this.userPaused = false;
          this.reducedMotion = false;
          this.held = false;
          this.endHoming();
          this.maybePlay();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      /*
       * Entry point for the card-level play button, which is pressed while this
       * slide is usually still off-frame. Deliberately does NOT call play()
       * directly: the carousel scrolls smoothly, and the IntersectionObserver
       * pauses anything under 50% visible, so an immediate play() would be
       * cancelled mid-scroll. Clearing the flags and warming the buffer lets the
       * observer start it the moment the slide actually arrives.
       */
      playFromCardTrigger(force, fromKeyboard) {
        this.userPaused = false;
        this.reducedMotion = false; // matches onToggleClick: explicit intent wins
        this.held = false;
        this.endHoming();
        // Set on every press, so a mouse press never inherits an earlier
        // keyboard one.
        this.jumpPressedFromKeyboard = !!fromKeyboard;
        this.warm();
        // force: flat-card mode, where the slide was display:none until this
        // click. The observer has not seen it yet so inView is still false, and
        // waiting for the next observation would delay the tap response. There
        // is no smooth scroll to be cancelled by playing immediately here.
        if (force || this.inView) this.maybePlay();
        // Already active (pressed while the jump button was still fading out):
        // the observer will not report the slide again, so hand focus over now.
        if (this.inView) this.takeFocusFromJump();
      }

      // The card-level play button, outside this element (see card-product.liquid).
      jumpButton() {
        return this.card ? this.card.querySelector('[data-card-video-jump]') : null;
      }

      /*
       * Jump button -> this toggle. The jump button hides the moment this slide
       * becomes active (card--video-slide-active, card-product-image-slider.css).
       * A hidden button that keeps focus shows no focus ring (WCAG 2.4.7), and
       * Enter on it plays the video again instead of pausing it. So keyboard
       * focus moves to this toggle, which sits in the same corner and is the
       * control from then on.
       *
       * Two signals count as keyboard, and a pointer press gives neither, so
       * mouse and touch are untouched. The press itself: a click with
       * event.detail === 0, which covers Enter, Space and assistive technology
       * (the handler at the bottom of this file). Or a keyboard focus ring on the
       * jump button, which covers tabbing onto a card whose carousel was left on
       * this slide: the slide activates as focus scrolls the card into view, and
       * the jump button would otherwise hide with focus still on it.
       *
       * Only if focus is still on the jump button, so tabbing on or clicking
       * elsewhere while the carousel scrolls cancels it. preventScroll because in
       * carousel mode this runs with the slide only half in: scrolling the toggle
       * into view would cut the smooth scroll short.
       */
      takeFocusFromJump() {
        const pressedFromKeyboard = this.jumpPressedFromKeyboard;
        this.jumpPressedFromKeyboard = false;
        const jump = this.jumpButton();
        if (!jump || document.activeElement !== jump) return;
        if (pressedFromKeyboard || hasKeyboardFocus(jump)) this.toggle.focus({ preventScroll: true });
      }

      /*
       * This toggle -> jump button, for flat-card mode only. A pause there takes
       * the video overlay down (display:none) with the toggle inside it, which
       * would drop keyboard focus to the page. Runs before the overlay goes,
       * while the focus is still readable. The jump button is visibility:hidden
       * while card--video-slide-active is on, so that class comes off first
       * instead of a frame later when the observer sees the overlay gone.
       *
       * Deliberately not called from stopAndReset(). That runs because a
       * different card started playing, so the shopper's attention is on that
       * card, and moving focus back here would drag a screen reader's cursor
       * across the page.
       */
      returnFocusToJump() {
        const jump = this.jumpButton();
        if (!jump || !hasKeyboardFocus(this.toggle)) return;
        this.card.classList.remove('card--video-slide-active');
        jump.focus({ preventScroll: true });
      }

      onPlay() {
        // One video at a time: this one starting stops the card that played last.
        // Keyed on the play event, so every route in (swipe, toggle, card-level
        // button, resume) goes through here.
        if (activeCardVideo && activeCardVideo !== this) activeCardVideo.stopAndReset();
        activeCardVideo = this;
        this.setPlayingState(true);
        this.startTicking();
      }

      /*
       * Another card's video has started. Stop this one (pause AND rewind, so it
       * starts from the top next time) and put its card back on the first slide:
       * scroll the carousel home, or in flat-card mode take the video overlay
       * down so the card image shows again.
       *
       * Two holds stop it restarting by itself, because a restart here stops
       * the card the shopper just chose:
       *
       * - `held`: the video slide is on screen right now. The pause below fires
       *   onPause, whose auto-resume would play it again while the slide is
       *   still at least half visible. The observer lifts it once the slide
       *   leaves the frame. The only hold a flat card needs.
       * - `homing`: the carousel is on its way home, and the trip crosses every
       *   slide between here and the first. That includes the video slide when
       *   the carousel was parked BEYOND it (a shopper who clicked through to
       *   the last image). `held` cannot cover that: the slide is off screen
       *   when this runs and only crosses the frame later, where the observer
       *   would autoplay it (seen on draft v20.5, 2026-09-21). Lifted when the
       *   carousel gets home, or by an explicit play.
       */
      stopAndReset() {
        this.held = this.inView;
        this.userPaused = false;
        this.stopTicking();
        if (this.video) {
          this.video.pause();
          this.video.currentTime = 0;
        }
        if (this.ring) this.ring.style.strokeDashoffset = '';

        // Flat-card mode: only ever added there, so a no-op on carousel cards.
        const host = this.flatHost();
        if (host) host.classList.remove('card--flat-video-playing');

        const list = this.scroller;
        if (list && list.scrollLeft !== 0) {
          this.homing = true;
          list.addEventListener('scroll', this.onHomingScroll, { passive: true });
          // Reduced-motion users get the jump without the slide: this motion is
          // on a card they did not touch.
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          if (typeof list.scrollTo === 'function') {
            list.scrollTo({ left: 0, behavior });
          } else {
            list.scrollLeft = 0;
          }
          this.onHomingScroll(); // an instant scroll is already home
        }
      }

      onHomingScroll() {
        if (this.scroller.scrollLeft < 1) this.endHoming();
      }

      endHoming() {
        this.homing = false;
        if (this.scroller) this.scroller.removeEventListener('scroll', this.onHomingScroll);
      }

      onPause() {
        this.setPlayingState(false);
        this.stopTicking();
        // Resume after an *external* pause (e.g. window.pauseAllMedia() fired by
        // the quick-add modal or cart drawer) while the slide is still on screen
        // and the user has not paused it themselves.
        if (!this.userPaused && !this.reducedMotion && !this.held && !this.homing && this.inView && !this.resuming) {
          this.resuming = true;
          requestAnimationFrame(() => {
            this.resuming = false;
            if (!this.userPaused && this.inView && this.video && this.video.paused) this.maybePlay();
          });
        }
      }

      setPlayingState(playing) {
        this.classList.toggle('is-paused', !playing);
        this.toggle.setAttribute('aria-pressed', String(playing));
        this.toggle.setAttribute('aria-label', playing ? this.labelPause : this.labelPlay);

        /*
         * Flat-card mode (a section with the card image carousel switched off):
         * the video slide is an overlay on the card image rather than a slide
         * you scroll to, so a user pause has to take the overlay back down and
         * return the shopper to that image.
         *
         * Gated on userPaused deliberately. onPause also fires for pauses the
         * shopper did not ask for — window.pauseAllMedia() from the quick-add
         * modal and cart drawer, and the IntersectionObserver pausing a card
         * scrolled off-screen — and both of those resume on their own. Tearing
         * the overlay down for those would flash the card image mid-playback.
         */
        if (!playing && this.userPaused && this.isFlatCard()) {
          const host = this.flatHost();
          if (host) {
            this.returnFocusToJump();
            host.classList.remove('card--flat-video-playing');
          }
        }
      }

      /*
       * The node that carries card--flat-video-playing. MUST resolve to the same
       * element as the jump handler's copy of this expression below: .card-wrapper
       * and .card are both ancestors of the slider, so the CSS matches either way
       * and a mismatch does not break the reveal — it breaks only the teardown,
       * silently leaving the video overlay up after the shopper hits pause.
       */
      flatHost() {
        return this.closest('.card') || this.closest('.card-wrapper');
      }

      // The flag lives on the section wrapper (see featured-collection-experience-v4),
      // because cards are injected from the collection section's render and carry
      // no knowledge of the section they land in.
      isFlatCard() {
        return !!this.closest('[data-card-slider-disabled]');
      }

      startTicking() {
        this.stopTicking();
        this.raf = requestAnimationFrame(this.tick);
      }

      stopTicking() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      tick() {
        const duration = this.video && this.video.duration;
        if (this.ring && duration && isFinite(duration)) {
          const pct = Math.max(0, Math.min(100, (this.video.currentTime / duration) * 100));
          // pathLength="100" normalises the path, so offset = remaining percent.
          this.ring.style.strokeDashoffset = (100 - pct).toFixed(2);
        }
        this.raf = requestAnimationFrame(this.tick);
      }
    }
  );
}

/*
 * Card-level play button -> jump to the video slide.
 *
 * Delegated on document and window-guarded rather than wired per card: grids are
 * replaced wholesale by facets, the homepage switchers and the colour swatch
 * swap, and <script> tags inside injected HTML never execute. Same rule the
 * quick-add overlay follows.
 */
if (!window.__cardVideoJumpInitialized) {
  window.__cardVideoJumpInitialized = true;

  document.addEventListener('click', (event) => {
    const jump = event.target.closest('[data-card-video-jump]');
    if (!jump) return;

    // The card's title link spans the whole card via ::after; keep this off it.
    event.preventDefault();
    event.stopPropagation();

    // Enter, Space and assistive technology click with detail 0; a mouse or a
    // tap never does. Keyboard presses get focus handed to the in-slide toggle
    // once the slide is active (CardLoopVideo.takeFocusFromJump).
    const fromKeyboard = event.detail === 0;

    const card = jump.closest('.card-wrapper');
    const el = card && card.querySelector('card-loop-video');
    if (!el) return;

    /*
     * Flat-card mode: the section switched its card image carousels off, so
     * there is nothing to scroll to — the video slide is an overlay sitting on
     * the card image, kept at display:none until now. Reveal it and let the
     * IntersectionObserver (which only sees it once it is displayed) start
     * playback, exactly as it does when a slide is scrolled into frame.
     */
    if (jump.closest('[data-card-slider-disabled]')) {
      // Same expression as CardLoopVideo.flatHost(), and it has to stay that way:
      // this adds the class and that removes it, so resolving different nodes
      // (.card vs .card-wrapper — both are ancestors, so the CSS still matches)
      // leaves the overlay stuck up after pause. Not read off `el` because the
      // custom element may not have upgraded yet on a freshly injected card.
      const host = el.closest('.card') || card;
      host.classList.add('card--flat-video-playing');
      if (typeof el.playFromCardTrigger === 'function') el.playFromCardTrigger(true, fromKeyboard);
      return;
    }

    const slide = el.closest('li');
    const list = card.querySelector('.card-image-slider__list');
    if (list && slide) {
      const left = slide.offsetLeft - list.offsetLeft;
      if (typeof list.scrollTo === 'function') {
        list.scrollTo({ left, behavior: 'smooth' });
      } else {
        list.scrollLeft = left;
      }
    }

    // customElements upgrade is async; if this card was just injected the
    // element may not have its methods yet, in which case the observer will
    // still autoplay the slide once it arrives (and still hands keyboard focus
    // over, off the jump button's focus ring).
    if (typeof el.playFromCardTrigger === 'function') el.playFromCardTrigger(false, fromKeyboard);
  });
}
